import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { supportedLanguages, type SupportedLanguage } from "@/i18n/config";
import ScrollToHash from "./components/ScrollToHash";

const Index = lazy(() => import("./pages/Index"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const NotFound = lazy(() => import("./pages/NotFound"));

function LanguageRoute({ children }: { children: JSX.Element }) {
  const { lang } = useParams<{ lang: string }>();
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <NotFound />;
  }
  return children;
}

function LanguageRedirect() {
  // Always render Czech content at "/" for SEO consistency.
  // Googlebot and prerender should always see the Czech homepage at the root URL.
  // Language switching is handled via the UI language switcher.
  return <Index />;
}

const App = () => (
  <LazyMotion features={domAnimation} strict>
    <TooltipProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToHash />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<LanguageRedirect />} />
            <Route path="/produkty" element={<Products />} />
            <Route path="/produkt/:slug" element={<ProductDetail />} />
            <Route path="/galerie" element={<Gallery />} />
            <Route path="/:lang" element={<LanguageRoute><Index /></LanguageRoute>} />
            <Route path="/:lang/produkty" element={<LanguageRoute><Products /></LanguageRoute>} />
            <Route path="/:lang/produkt/:slug" element={<LanguageRoute><ProductDetail /></LanguageRoute>} />
            <Route path="/:lang/galerie" element={<LanguageRoute><Gallery /></LanguageRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </LazyMotion>
);

export default App;
